import { useState, useEffect, useCallback } from "react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Shield, ShieldAlert, ShieldCheck, ShieldX, Star, Trash2, RefreshCw, Loader2, Lock } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { format } from "date-fns";
import { fr } from "date-fns/locale";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";

interface Domain {
  id: string;
  domain_name: string | null;
  status: string | null;
  ssl_status: string | null;
  is_primary: boolean | null;
  created_at: string | null;
  domain_type: string | null;
  store_id: string | null;
}

interface MyDomainsTabProps {
  refreshKey?: number;
}

export const MyDomainsTab = ({ refreshKey }: MyDomainsTabProps) => {
  const [domains, setDomains] = useState<Domain[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [verifyingId, setVerifyingId] = useState<string | null>(null);
  const [retryingSslId, setRetryingSslId] = useState<string | null>(null);
  const { toast } = useToast();

  const fetchDomains = useCallback(async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const { data, error } = await supabase
        .from("domains")
        .select("*")
        .eq("user_id", user.id)
        .order("created_at", { ascending: false });

      if (error) throw error;
      setDomains(data || []);
    } catch (error) {
      console.error("Error fetching domains:", error);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => { fetchDomains(); }, [fetchDomains, refreshKey]);

  // Realtime subscription for automatic refresh on ssl_status or status changes
  useEffect(() => {
    const channel = supabase
      .channel("domains-ssl-changes")
      .on(
        "postgres_changes",
        { event: "UPDATE", schema: "public", table: "domains" },
        () => { fetchDomains(); }
      )
      .subscribe();

    return () => { supabase.removeChannel(channel); };
  }, [fetchDomains]);

  // Periodic DNS re-verification for active domains (every 5 minutes)
  useEffect(() => {
    const reVerifyActiveDomains = async () => {
      const activeDomains = domains.filter(d => d.status === "active" && d.domain_name);
      for (const domain of activeDomains) {
        try {
          const aValid = await verifyARecord(domain.domain_name!);
          if (aValid) continue; // Still valid

          const txtValid = await verifyTxtRecord(domain.domain_name!);
          if (txtValid) continue; // Still valid via TXT

          // DNS no longer points to Sokoby — revert to pending (non-breaking)
          console.warn(`[DNS Re-check] ${domain.domain_name} no longer resolves to Sokoby. Reverting to pending.`);
          await supabase.from("domains")
            .update({ status: "pending", ssl_status: "pending", updated_at: new Date().toISOString() })
            .eq("id", domain.id)
            .eq("status", "active"); // Only update if still active (race protection)

          toast({
            title: "DNS modifié",
            description: `${domain.domain_name} ne pointe plus vers Sokoby. Statut repassé en attente.`,
            variant: "destructive",
          });
        } catch {
          // Silent fail — re-verification is non-critical
        }
      }
      if (activeDomains.length > 0) {
        await fetchDomains();
      }
    };

    // Initial check after 30s, then every 5 minutes
    const initialTimeout = setTimeout(reVerifyActiveDomains, 30000);
    const interval = setInterval(reVerifyActiveDomains, 5 * 60 * 1000);

    return () => {
      clearTimeout(initialTimeout);
      clearInterval(interval);
    };
  }, [domains, fetchDomains, toast]);

  const simulateSslProvisioning = async (domainId: string, domainName: string): Promise<"active" | "error"> => {
    // Check if the domain resolves correctly (simulating SSL provisioning)
    try {
      const response = await fetch(`https://dns.google/resolve?name=${domainName}&type=A`);
      const data = await response.json();
      const pointsToSokoby = data.Answer?.some(
        (record: any) => record.type === 1 && record.data === "185.158.133.1"
      );
      return pointsToSokoby ? "active" : "error";
    } catch {
      return "error";
    }
  };

  const verifyARecord = async (domainName: string): Promise<boolean> => {
    try {
      const response = await fetch(`https://dns.google/resolve?name=${domainName}&type=A`);
      const data = await response.json();
      return !!data.Answer?.some(
        (record: any) => record.type === 1 && record.data === "185.158.133.1"
      );
    } catch {
      return false;
    }
  };

  const verifyTxtRecord = async (domainName: string): Promise<boolean> => {
    try {
      const response = await fetch(`https://dns.google/resolve?name=_sokoby-verify.${domainName}&type=TXT`);
      const data = await response.json();
      return !!data.Answer?.some(
        (record: any) => record.type === 16 && typeof record.data === "string" && record.data.includes("sokoby-verify=")
      );
    } catch {
      return false;
    }
  };

  const handleVerify = async (domain: Domain) => {
    setVerifyingId(domain.id);
    try {
      // Ownership check
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;
      const { data: record } = await supabase.from("domains").select("user_id").eq("id", domain.id).single();
      if (record?.user_id !== user.id) {
        toast({ title: "Accès refusé", variant: "destructive" });
        return;
      }

      await supabase
        .from("domains")
        .update({ status: "verifying", updated_at: new Date().toISOString() })
        .eq("id", domain.id);
      await fetchDomains();

      // Try A record first, then TXT fallback
      const aRecordValid = await verifyARecord(domain.domain_name || "");
      const txtRecordValid = !aRecordValid ? await verifyTxtRecord(domain.domain_name || "") : false;
      const verified = aRecordValid || txtRecordValid;

      if (verified) {
        // Domain activates immediately; SSL tracked independently
        const sslStatus = aRecordValid ? "provisioning" : "pending";
        await supabase
          .from("domains")
          .update({
            status: "active",
            ssl_status: sslStatus,
            updated_at: new Date().toISOString(),
          })
          .eq("id", domain.id);

        const method = aRecordValid ? "enregistrement A" : "enregistrement TXT";
        toast({
          title: "Domaine vérifié",
          description: `${domain.domain_name} activé via ${method}. SSL en cours de provisionnement.`,
        });

        // Non-blocking SSL provisioning (only if A record valid)
        if (aRecordValid) {
          setTimeout(async () => {
            try {
              const sslResult = await simulateSslProvisioning(domain.id, domain.domain_name || "");
              await supabase.from("domains")
                .update({ ssl_status: sslResult, updated_at: new Date().toISOString() })
                .eq("id", domain.id)
                .eq("status", "active");
            } catch (e) {
              console.warn("SSL update failed (non-blocking):", e);
            }
          }, 3000);
        }
      } else {
        await supabase
          .from("domains")
          .update({ status: "pending", ssl_status: "pending", updated_at: new Date().toISOString() })
          .eq("id", domain.id);

        toast({
          title: "DNS non configuré",
          description: "Le DNS ne pointe pas encore vers Sokoby. Essayez l'enregistrement A ou TXT.",
          variant: "destructive",
        });
      }
      await fetchDomains();
    } catch {
      await supabase
        .from("domains")
        .update({ ssl_status: "error", updated_at: new Date().toISOString() })
        .eq("id", domain.id);
      await fetchDomains();
      toast({ title: "Erreur", description: "Impossible de vérifier le domaine.", variant: "destructive" });
    } finally {
      setVerifyingId(null);
    }
  };

  const handleRetrySsl = async (domain: Domain) => {
    setRetryingSslId(domain.id);
    try {
      await supabase
        .from("domains")
        .update({ ssl_status: "pending", updated_at: new Date().toISOString() })
        .eq("id", domain.id);
      await fetchDomains();

      const sslResult = await simulateSslProvisioning(domain.id, domain.domain_name || "");

      await supabase
        .from("domains")
        .update({ ssl_status: sslResult, updated_at: new Date().toISOString() })
        .eq("id", domain.id);

      toast({
        title: sslResult === "active" ? "SSL activé" : "Échec SSL",
        description: sslResult === "active"
          ? `HTTPS activé pour ${domain.domain_name}`
          : "Impossible de provisionner le SSL. Vérifiez que le DNS est correct.",
        variant: sslResult === "active" ? "default" : "destructive",
      });
      await fetchDomains();
    } catch {
      toast({ title: "Erreur", description: "Échec de la tentative SSL.", variant: "destructive" });
    } finally {
      setRetryingSslId(null);
    }
  };

  const handleSetPrimary = async (domain: Domain) => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      if (domain.store_id) {
        await supabase.from("domains").update({ is_primary: false }).eq("store_id", domain.store_id);
      } else {
        await supabase.from("domains").update({ is_primary: false }).eq("user_id", user.id);
      }
      await supabase.from("domains").update({ is_primary: true }).eq("id", domain.id);

      toast({ title: "Domaine principal mis à jour" });
      await fetchDomains();
    } catch {
      toast({ title: "Erreur", variant: "destructive" });
    }
  };

  const handleRemove = async (domainId: string) => {
    try {
      const { error } = await supabase.from("domains").delete().eq("id", domainId);
      if (error) throw error;
      toast({ title: "Domaine supprimé" });
      await fetchDomains();
    } catch {
      toast({ title: "Erreur", description: "Impossible de supprimer le domaine.", variant: "destructive" });
    }
  };

  const statusBadge = (status: string | null) => {
    switch (status) {
      case "active": return <Badge className="bg-green-100 text-green-800 hover:bg-green-100">Actif</Badge>;
      case "verifying": return <Badge className="bg-blue-100 text-blue-800 hover:bg-blue-100">Vérification</Badge>;
      case "pending": return <Badge variant="secondary">En attente</Badge>;
      case "failed": return <Badge variant="destructive">Échoué</Badge>;
      default: return <Badge variant="outline">Inconnu</Badge>;
    }
  };

  const sslBadge = (domain: Domain) => {
    const ssl = domain.ssl_status;
    switch (ssl) {
      case "active":
        return (
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <span className="flex items-center gap-1.5 text-green-600 text-sm font-medium">
                  <ShieldCheck className="h-4 w-4" />
                  HTTPS
                </span>
              </TooltipTrigger>
              <TooltipContent>SSL actif — HTTPS forcé</TooltipContent>
            </Tooltip>
          </TooltipProvider>
        );
      case "provisioning":
      case "pending":
        return (
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <span className="flex items-center gap-1.5 text-amber-600 text-sm">
                  <Shield className="h-4 w-4" />
                  {ssl === "provisioning" ? "Provisionnement" : "En cours"}
                </span>
              </TooltipTrigger>
              <TooltipContent>
                {ssl === "provisioning"
                  ? "SSL en cours de provisionnement — le domaine est déjà actif"
                  : "En attente de vérification DNS pour le SSL"}
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        );
      case "error":
        return (
          <div className="flex items-center gap-2">
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <span className="flex items-center gap-1.5 text-destructive text-sm font-medium">
                    <ShieldX className="h-4 w-4" />
                    Erreur
                  </span>
                </TooltipTrigger>
                <TooltipContent>Le SSL n'a pas pu être provisionné</TooltipContent>
              </Tooltip>
            </TooltipProvider>
            <Button
              variant="ghost"
              size="sm"
              className="h-6 px-2 text-xs"
              onClick={() => handleRetrySsl(domain)}
              disabled={retryingSslId === domain.id}
            >
              {retryingSslId === domain.id ? <Loader2 className="h-3 w-3 animate-spin" /> : <RefreshCw className="h-3 w-3" />}
              <span className="ml-1">Réessayer</span>
            </Button>
          </div>
        );
      default:
        return (
          <span className="flex items-center gap-1.5 text-muted-foreground text-sm">
            <ShieldAlert className="h-4 w-4" />
            Inactif
          </span>
        );
    }
  };

  const domainUrl = (domain: Domain) => {
    if (domain.ssl_status === "active") {
      return `https://${domain.domain_name}`;
    }
    return `http://${domain.domain_name}`;
  };

  if (isLoading) {
    return <div className="flex justify-center py-12"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>;
  }

  if (domains.length === 0) {
    return (
      <div className="text-center py-12 space-y-2">
        <p className="text-muted-foreground">Aucun domaine connecté pour le moment.</p>
        <p className="text-sm text-muted-foreground">Utilisez l'onglet « Connecter » pour commencer.</p>
      </div>
    );
  }

  return (
    <div className="rounded-md border">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Domaine</TableHead>
            <TableHead>Statut</TableHead>
            <TableHead>SSL</TableHead>
            <TableHead>Principal</TableHead>
            <TableHead>Date d'ajout</TableHead>
            <TableHead className="text-right">Actions</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {domains.map((domain) => (
            <TableRow key={domain.id}>
              <TableCell>
                <div className="flex items-center gap-2">
                  {domain.ssl_status === "active" && <Lock className="h-3.5 w-3.5 text-green-600" />}
                  <a
                    href={domainUrl(domain)}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="font-medium hover:underline"
                  >
                    {domain.domain_name}
                  </a>
                </div>
              </TableCell>
              <TableCell>{statusBadge(domain.status)}</TableCell>
              <TableCell>{sslBadge(domain)}</TableCell>
              <TableCell>
                {domain.is_primary ? (
                  <Star className="h-5 w-5 text-amber-500 fill-amber-500" />
                ) : (
                  <span className="text-muted-foreground text-sm">—</span>
                )}
              </TableCell>
              <TableCell className="text-sm text-muted-foreground">
                {domain.created_at ? format(new Date(domain.created_at), "d MMM yyyy", { locale: fr }) : "—"}
              </TableCell>
              <TableCell className="text-right">
                <div className="flex justify-end gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleVerify(domain)}
                    disabled={verifyingId === domain.id}
                    title="Vérifier le DNS"
                  >
                    {verifyingId === domain.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
                  </Button>
                  {!domain.is_primary && (
                    <Button variant="outline" size="sm" onClick={() => handleSetPrimary(domain)} title="Définir comme principal">
                      <Star className="h-4 w-4" />
                    </Button>
                  )}
                  <Button variant="outline" size="sm" onClick={() => handleRemove(domain.id)} className="text-destructive hover:text-destructive" title="Supprimer">
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
};
